import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

import { appInject } from 'src/app/app-injector';
import { WINDOW } from 'src/app/utils/window.token';

import { LazyHostComponent } from '../lazy-host/lazy-host.component';
import { LazyComponentId } from '../services/lazy-components/lazy-components-registry';
import { LoggerService } from '../services/logger/logger.service';

import { DevModeService } from './dev-mode/dev-mode.service';

const DEV_LAYER_KEY_COMBO = 'DEV';

const DEV_LAYER_KEY_COMBO_CODES = new Set(Array.from(DEV_LAYER_KEY_COMBO, char => `Key${char}`));

const DEV_LAYER_KEY_COMBO_TIMEOUT = 500;

@Component({
  selector: 'cc-dev',
  templateUrl: './dev.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LazyHostComponent]
})
export class DevComponent implements OnInit {
  public readonly devLayerComponentId = LazyComponentId.DevLayer;

  public isDevMode = false;

  private keyComboTimeout = 0;
  private keyCombo = '';

  public constructor(
    private readonly devModeService: DevModeService,
    private readonly loggerService: LoggerService,
    private readonly changeDetector: ChangeDetectorRef,
    @Inject(WINDOW) private readonly window: Window
  ) {}

  public ngOnInit(): void {
    // get current dev mode state from service
    this.devModeService.isDevMode$.pipe(takeUntilDestroyed(appInject(DestroyRef))).subscribe(isDevMode => {
      this.isDevMode = isDevMode;
      this.logDevModeStatus();
      this.changeDetector.markForCheck();
    });
    // react on key combo
    fromEvent<KeyboardEvent>(this.window.document, 'keydown')
      .pipe(takeUntilDestroyed(appInject(DestroyRef)))
      .subscribe(event => {
        this.handleKeyCombo(event);
      });
  }

  private logDevModeStatus(): void {
    this.loggerService.info(`DevMode is ${this.isDevMode ? 'enabled' : 'disabled'}`);
  }

  /**
   * Toggles DevMode if [CTRL-SHIFT-ALT, D, E, V] is pressed within 500ms
   */
  private handleKeyCombo(event: KeyboardEvent): void {
    if (DEV_LAYER_KEY_COMBO_CODES.has(event.code) && event.shiftKey && event.altKey && event.ctrlKey) {
      if (this.keyComboTimeout) {
        this.window.clearTimeout(this.keyComboTimeout);
      }
      this.keyCombo += event.code.at(-1);
      this.keyComboTimeout = this.window.setTimeout(() => (this.keyCombo = ''), DEV_LAYER_KEY_COMBO_TIMEOUT);
      if (this.keyCombo === DEV_LAYER_KEY_COMBO) {
        this.devModeService.toggleDevMode();
        this.keyCombo = '';
        this.window.clearTimeout(this.keyComboTimeout);
      } else if (this.keyCombo.length >= DEV_LAYER_KEY_COMBO.length) {
        this.keyCombo = '';
      }
    }
  }
}
